/**
 * TDBReconciliationDrift — Daily diff of Mickky's MIS vs ScaleBoard MTD.
 *
 * Reads `GET /api/tdb-mis/reconciliation/drift?period=&doctrine=source`.
 * Default doctrine is 'source' to match Mickky's 1/3 split (so shared
 * costs don't generate false-positive drift).
 *
 * UX:
 *   • Period picker · doctrine picker · refresh
 *   • Summary chip: green tick if all cells within ₹100, red badge with count
 *   • Per-city × per-bucket diff table with 3-column layout
 *   • Click a red row → drill-down modal with underlying transactions
 *   • "Run reconciliation now" button (CEO + TDB-leader · audit-logged)
 *   • Stale-banner if Mickky's Excel is > 5 days old
 */
import { useEffect, useState, useCallback } from 'react';
import {
  GitCompare, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Send, X,
} from 'lucide-react';
import api from '@/lib/api';

function thisMonthYM() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return ist.toISOString().slice(0, 7);
}

function fmtRupees(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '₹0';
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function fmtSigned(n) {
  if (n === null || n === undefined) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${fmtRupees(n)}`;
}

export default function TDBReconciliationDrift({ className = '' }) {
  const [period, setPeriod] = useState(thisMonthYM());
  const [doctrine, setDoctrine] = useState('source');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');
  const [drillCell, setDrillCell] = useState(null);
  const [drillData, setDrillData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/tdb-mis/reconciliation/drift', {
        params: { period, doctrine },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [period, doctrine]);

  useEffect(() => { load(); }, [load]);

  const runNow = useCallback(async () => {
    if (!window.confirm('Trigger the daily reconciliation now? It will send email + WhatsApp.')) return;
    setRunning(true);
    setErr('');
    try {
      const r = await api.post('/tdb-mis/reconciliation/run-now');
      alert(`Run complete · ${r.data.summary?.message || 'done'}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  }, [load]);

  const openDrill = useCallback(async (row) => {
    if (row.bucket === '_income' || row.bucket === '_gop') return;
    setDrillCell(row);
    setDrillData(null);
    try {
      const r = await api.get('/tdb-mis/reconciliation/transactions', {
        params: { city: row.city, bucket: row.bucket, period },
      });
      setDrillData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Drill-down failed');
    }
  }, [period]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const stale = !!data?.mickky_stale;
  const allGreen = summary.red === 0 && summary.total_cells > 0;

  return (
    <div
      data-testid="tdb-reconciliation-drift"
      className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <GitCompare className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Reconciliation Drift</h3>
          <span
            data-testid="tdb-recon-summary-chip"
            className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
              stale
                ? 'bg-amber-50 text-amber-800 border-amber-200'
                : allGreen
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            {stale ? '⏳ Awaiting Mickky upload' : allGreen ? `✓ All ${summary.total_cells} cells reconcile` : `🔴 ${summary.red} drifts · ${fmtRupees(summary.gap_total)} gap`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="tdb-recon-doctrine"
            value={doctrine}
            onChange={(e) => setDoctrine(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          >
            <option value="source">📜 vs Mickky (Source)</option>
            <option value="scaleboard">📐 vs ScaleBoard doctrine</option>
          </select>
          <input
            data-testid="tdb-recon-period"
            type="month"
            value={period}
            max={thisMonthYM()}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          />
          <button
            data-testid="tdb-recon-refresh"
            onClick={load}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            data-testid="tdb-recon-run-now"
            onClick={runNow}
            disabled={running}
            className="text-xs px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-60"
            title="Trigger daily reconciliation now · sends email + WhatsApp"
          >
            <Send className="w-3 h-3" /> {running ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>

      {/* Stale banner */}
      {stale && (
        <div
          data-testid="tdb-recon-stale-banner"
          className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-1.5"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold">Awaiting fresh Mickky upload</span>
            {data?.mickky_age_days !== null && data?.mickky_age_days !== undefined
              ? ` · last upload ${data.mickky_age_days}d ago. Reconciliation deferred until Mickky uploads.`
              : ' · no MIS Excel uploaded for this period yet.'}
          </div>
        </div>
      )}

      {err && (
        <div data-testid="tdb-recon-error" className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          {err}
        </div>
      )}

      {/* Diff table */}
      {!stale && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="tdb-recon-diff-table">
            <thead>
              <tr className="text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="text-left py-1.5 px-2 font-medium"></th>
                <th className="text-left py-1.5 px-2 font-medium">City</th>
                <th className="text-left py-1.5 px-2 font-medium">Bucket</th>
                <th className="text-right py-1.5 px-2 font-medium">Mickky</th>
                <th className="text-right py-1.5 px-2 font-medium">ScaleBoard</th>
                <th className="text-right py-1.5 px-2 font-medium">Gap (₹)</th>
                <th className="text-right py-1.5 px-2 font-medium">Δ %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isRed = r.status === 'red';
                const isDrillable = r.bucket !== '_income' && r.bucket !== '_gop';
                return (
                  <tr
                    key={i}
                    data-testid={`tdb-recon-row-${i}`}
                    onClick={() => isRed && isDrillable && openDrill(r)}
                    className={`border-t border-gray-100 ${isRed && isDrillable ? 'cursor-pointer hover:bg-red-50' : ''}`}
                  >
                    <td className="py-1 px-2">
                      {isRed ? <XCircle className="w-3.5 h-3.5 text-red-700" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                    </td>
                    <td className="py-1 px-2 font-medium text-gray-800">{r.city}</td>
                    <td className="py-1 px-2 text-gray-700">{r.bucket_label}</td>
                    <td className="text-right py-1 px-2 text-gray-700">{fmtRupees(r.mickky)}</td>
                    <td className="text-right py-1 px-2 text-gray-700">{fmtRupees(r.scaleboard)}</td>
                    <td className={`text-right py-1 px-2 font-medium ${isRed ? 'text-red-700' : 'text-gray-600'}`}>
                      {fmtSigned(r.gap)}
                    </td>
                    <td className={`text-right py-1 px-2 ${isRed ? 'text-red-700' : 'text-gray-500'}`}>
                      {r.gap_pct >= 0 ? '+' : ''}{(r.gap_pct || 0).toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-2 text-[10px] text-gray-500">
        Tolerance ₹{data?.tolerance || 100} · diff vs <span className="font-mono">{doctrine}</span> doctrine · click any red row for drill-down · auto-runs daily 08:30 IST during 1-Jun → 30-Jun parallel-run
      </div>

      {/* Drill-down modal */}
      {drillCell && (
        <div
          data-testid="tdb-recon-drill-modal"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setDrillCell(null)}
        >
          <div
            className="bg-white rounded-2xl border border-gray-200 max-w-3xl w-full max-h-[80vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">
                Drill-down · {drillCell.city} · {drillCell.bucket_label}
              </h4>
              <button onClick={() => setDrillCell(null)} className="text-gray-400 hover:text-gray-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <div className="font-semibold text-gray-700 mb-1">Mickky lines (₹{drillCell.mickky.toLocaleString('en-IN')})</div>
                {(drillData?.mickky_lines || []).length === 0 ? (
                  <div className="text-gray-400 italic">No lines</div>
                ) : (
                  <ul className="space-y-1">
                    {(drillData?.mickky_lines || []).map((l, i) => (
                      <li key={i} className="flex justify-between py-0.5 border-b border-gray-50">
                        <span>{l.label}</span>
                        <span className="font-medium">{fmtRupees(l.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="font-semibold text-gray-700 mb-1">ScaleBoard transactions (₹{drillCell.scaleboard.toLocaleString('en-IN')})</div>
                {(drillData?.scaleboard_transactions || []).length === 0 ? (
                  <div className="text-gray-400 italic">No transactions entered yet</div>
                ) : (
                  <ul className="space-y-1">
                    {(drillData?.scaleboard_transactions || []).slice(0, 30).map((t, i) => (
                      <li key={i} className="py-0.5 border-b border-gray-50">
                        <div className="flex justify-between">
                          <span>{t.date} · {t.category}</span>
                          <span className="font-medium">{fmtRupees(t.amount)}</span>
                        </div>
                        {t.notes && <div className="text-gray-400 text-[10px]">{t.notes}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
